import { useState } from "react";
import { Check, Loader2, Users } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { StageDates } from "@/components/StageDates";
import { DayField } from "@/components/DayField";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { api, apiError, fmtDay, fmtWhen, JOURNEY_COLORS, TIMELINE_COLORS, TIMELINE_LABELS } from "@/lib/api";
import { toastWithUndo } from "@/lib/undo";

export const JourneyTimeline = ({ project, meta, canEdit, onChanged }) => {
  // A use case being bought in travels the procurement road; the portal says which.
  const stages = project?.sub_stages || meta?.sub_stages || meta?.journey_stages || [];
  const help =
    project?.stage_route === "procurement"
      ? meta?.procurement_sub_stage_help || {}
      : meta?.sub_stage_help || meta?.journey_help || {};
  const current = project?.phase;
  const currentIndex = stages.indexOf(current);
  const [stage, setStage] = useState(current || stages[0]);
  const [on, setOn] = useState("");
  const [prevDone, setPrevDone] = useState("");
  const [expected, setExpected] = useState("");
  const [reason, setReason] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [ask, setAsk] = useState(false);
  const datesMandatory = !!project?.is_target_value;
  const held = (project?.stage_dates?.[stage] || {}).expected_on || "";
  const isRevision = !!expected.trim() && !!held && expected.trim() !== held;

  const check = () => {
    if (datesMandatory && stage !== current) {
      if (current && !prevDone.trim()) {
        toast.error(`This is a target-value use case — give the date ${current} was completed.`);
        return;
      }
      if (!expected.trim()) {
        toast.error(`This is a target-value use case — give the expected date for ${stage}.`);
        return;
      }
    }
    if (isRevision && !reason.trim()) {
      toast.error(
        `${stage} was committed for ${fmtDay(held)}. Please say why the date is moving — the portal keeps both dates on record.`,
      );
      return;
    }
    setAsk(true);
  };

  const save = async () => {
    setAsk(false);
    setBusy(true);
    try {
      const { data } = await api.post(`/projects/${project.id}/journey`, {
        stage,
        on: on.trim() || null,
        prev_completed_on: prevDone.trim() || null,
        expected_on: expected.trim() || null,
        revision_reason: reason.trim() || null,
        note: note.trim() || null,
      });
      const wasStage = current;
      const undo = async () => {
        if (data.awaiting_verification && data.pending?.id) {
          await api.post(`/pending/${data.pending.id}/withdraw`);
        } else if (wasStage) {
          await api.post(`/projects/${project.id}/journey`, {
            stage: wasStage,
            note: "taken back — the earlier sub-stage stands",
          });
        }
        onChanged?.();
      };
      toastWithUndo(
        data.awaiting_verification
          ? `Sent to your SPOC for approval — the register still reads ${wasStage || "as before"}.`
          : isRevision
            ? `${stage} moved from ${fmtDay(held)} to ${expected.trim()} — kept on record as a revision.`
            : on.trim()
              ? `Sub-stage set to ${stage} as on ${on.trim()}.`
              : `Sub-stage set to ${stage}. Today's date has been kept as the date of entry.`,
        undo,
        { done: "Sub-stage put back." },
      );
      setNote("");
      setOn("");
      setPrevDone("");
      setExpected("");
      setReason("");
      onChanged?.();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="journey-block">
      <div className="label-caps mb-2">Where this use case has reached</div>

      <div className="flex flex-wrap gap-1.5" data-testid="journey-track">
        {stages.map((s, i) => {
          const done = currentIndex >= 0 && i <= currentIndex;
          const isNow = s === current;
          return (
            <span
              key={s}
              title={help[s] || s}
              data-testid={`journey-step-${i}`}
              className={`flex items-center gap-1.5 border px-2 py-1 text-[11px] transition-colors duration-200 ${
                isNow ? "font-bold" : "text-muted-foreground"
              }`}
              style={{
                borderColor: done ? JOURNEY_COLORS[s] : "hsl(var(--border))",
                color: isNow ? JOURNEY_COLORS[s] : undefined,
                backgroundColor: isNow ? "hsl(var(--muted))" : undefined,
              }}
            >
              {done && <Check className="h-3 w-3" style={{ color: JOURNEY_COLORS[s] }} />}
              {s}
            </span>
          );
        })}
      </div>

      <p className="font-data mt-2 text-[11px] text-muted-foreground">
        {current
          ? `${current} since ${fmtDay(project.phase_on)}${
              project.phase_expected_on ? ` · expected to close ${fmtDay(project.phase_expected_on)}` : ""
            }${
              project.phase_source === "register"
                ? " · taken from the unit register, please confirm"
                : project.phase_by
                  ? ` · set by ${project.phase_by}`
                  : ""
            }`
          : "Sub-stage not set yet."}
      </p>

      <div className="mt-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="label-caps mb-1.5">Major sub-stage dates</div>
          {project?.milestone_timeline && (
            <span
              className="border px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider"
              style={{
                color: TIMELINE_COLORS[project.milestone_timeline.overall],
                borderColor: TIMELINE_COLORS[project.milestone_timeline.overall],
              }}
              data-testid="detail-timeline-verdict"
            >
              {TIMELINE_LABELS[project.milestone_timeline.overall]}
              {project.milestone_timeline.worst_slip_days > 0
                ? ` · ${project.milestone_timeline.worst_slip_days} day(s) beyond plan`
                : ""}
            </span>
          )}
        </div>
        <StageDates project={project} />
      </div>

      {canEdit && (
        <div
          className="mt-3 grid gap-2 border border-border bg-card p-3 sm:grid-cols-2 lg:grid-cols-4"
          data-testid="journey-form"
        >
          <div className="lg:col-span-2">
            <label className="label-caps">Sub-stage now reached</label>
            <Select value={stage} onValueChange={setStage}>
              <SelectTrigger
                className="mt-1 h-10 rounded-none text-xs"
                data-testid="journey-stage-select"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="rounded-none bg-popover">
                {stages.map((s) => (
                  <SelectItem key={s} value={s}>
                    {s}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="label-caps">Reached on</label>
            <DayField
              className="mt-1"
              testId="journey-date"
              value={on}
              onChange={setOn}
            />
          </div>
          <div>
            <label className="label-caps">Previous closed{datesMandatory ? " *" : ""}</label>
            <DayField
              className="mt-1"
              testId="journey-prev-completed"
              value={prevDone}
              onChange={setPrevDone}
            />
          </div>
          <div>
            <label className="label-caps">Expected by{datesMandatory ? " *" : ""}</label>
            <DayField
              className="mt-1"
              testId="journey-expected"
              value={expected}
              onChange={setExpected}
            />
          </div>
          <div className="lg:col-span-2">
            <label className="label-caps">Note</label>
            <Input
              placeholder="Anything the reviewer should know (optional)"
              className="mt-1 h-10 rounded-none text-xs"
              data-testid="journey-note"
              value={note}
              onChange={(e) => setNote(e.target.value)}
            />
          </div>
          <div className="flex items-end sm:col-span-2 lg:col-span-4">
            <Button
              className="h-10 w-full rounded-none text-xs font-bold uppercase tracking-widest sm:w-auto"
              disabled={busy || !stage}
              data-testid="journey-save"
              onClick={check}
            >
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Set sub-stage"}
            </Button>
          </div>
          {isRevision && (
            <div className="sm:col-span-2 lg:col-span-4" data-testid="journey-revision-block">
              <label className="label-caps">
                Why is the {stage} date moving from {fmtDay(held)}? (compulsory)
              </label>
              <Input
                placeholder="e.g. vendor delivery slipped, awaiting order amendment"
                className="mt-1 h-10 rounded-none text-xs"
                data-testid="journey-revision-reason"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
              />
              <p className="mt-1 text-[10px] text-muted-foreground">
                The portal keeps {fmtDay(held)} on record as the date first committed and marks this
                use case as revised, so a reviewer can see both.
              </p>
            </div>
          )}
          <p className="text-[10px] leading-relaxed text-muted-foreground sm:col-span-2 lg:col-span-4">
            {datesMandatory
              ? "This is a Pravartanam target-value use case — the date the previous sub-stage was completed and the expected date of this one are both compulsory."
              : "The two dates are optional here, but filling them keeps the review pack complete."}{" "}
            Dates are written as dd.mm.yyyy. Leave "reached on" blank and the portal keeps today as
            the day of entry.
            {project?.stage_route === "procurement" &&
              " This use case is being bought in, so it travels the procurement road: In-Pipeline, TS preparation, Under Approval, Tendering Stage, Order Placement, Under Execution, Completed."}
          </p>
        </div>
      )}

      <ConfirmDialog
        open={ask}
        onOpenChange={setAsk}
        title="Are you sure?"
        description={`${project?.code} — ${project?.title}. You can take this back for 20 seconds after saving.`}
        changes={[
          { label: "Sub-stage", from: current, to: stage },
          ...(expected.trim()
            ? [{ label: `${stage} expected by`, from: held || null, to: expected.trim() }]
            : []),
          ...(prevDone.trim() && current
            ? [{ label: `${current} completed on`, from: null, to: prevDone.trim() }]
            : []),
        ]}
        confirmLabel="Yes, set the sub-stage"
        testId="journey-confirm"
        onConfirm={save}
      />

      {project?.journey?.length > 0 && (
        <ol className="mt-3 space-y-1.5" data-testid="journey-history">
          {[...project.journey].reverse().map((e, i) => (
            <li
              key={`${e.stage}-${e.at}-${i}`}
              className="border-l-2 pl-3 text-[11px]"
              style={{ borderColor: JOURNEY_COLORS[e.stage] }}
            >
              <b>{e.stage}</b> on {fmtDay(e.on)}
              {e.date_captured ? " (date of entry)" : ""} — {e.by}
              {e.prev_completed_on ? ` · previous sub-stage closed ${fmtDay(e.prev_completed_on)}` : ""}
              {e.expected_on ? ` · expected by ${fmtDay(e.expected_on)}` : ""}
              {e.revision_of
                ? ` · revised from ${fmtDay(e.revision_of)}${
                    e.revision_reason ? ` because ${e.revision_reason}` : ""
                  }`
                : ""}
              {e.note ? ` · ${e.note}` : ""}
              <span className="font-data block text-[10px] text-muted-foreground">
                recorded {fmtWhen(e.at)}
              </span>
            </li>
          ))}
        </ol>
      )}
    </div>
  );
};

export const RecordersBlock = ({ project, canEdit, onChanged }) => {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const list = project?.recorders || [];

  const save = async (emails) => {
    setBusy(true);
    try {
      await api.put(`/projects/${project.id}/recorders`, { emails });
      toast.success("Saved who may fill readings.");
      setEmail("");
      onChanged?.();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="recorders-block">
      <div className="label-caps mb-2">Who may fill the day-wise readings</div>
      <p className="text-[11px] text-muted-foreground">
        The unit SPOC and SDTD can always fill readings. Designate anyone else from your unit here —
        they must already be a portal user.
      </p>
      <div className="mt-2 flex flex-wrap gap-2">
        {list.length === 0 && (
          <span className="text-xs text-muted-foreground">Nobody designated yet.</span>
        )}
        {list.map((r) => (
          <span
            key={r.email}
            className="flex items-center gap-2 border border-border bg-muted px-2 py-1 text-[11px]"
          >
            <Users className="h-3 w-3 text-muted-foreground" />
            {r.name}
            {canEdit && (
              <button
                type="button"
                data-testid={`remove-recorder-${r.email}`}
                className="text-muted-foreground hover:text-primary"
                onClick={() => save(list.filter((x) => x.email !== r.email).map((x) => x.email))}
              >
                ×
              </button>
            )}
          </span>
        ))}
      </div>
      {canEdit && (
        <div className="mt-2 flex gap-2">
          <Input
            placeholder="officer@sail.in"
            className="h-10 max-w-xs rounded-none text-xs"
            data-testid="recorder-email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
          <Button
            variant="outline"
            className="h-10 rounded-none text-xs"
            disabled={busy || !email.trim()}
            data-testid="add-recorder"
            onClick={() => save([...list.map((x) => x.email), email.trim().toLowerCase()])}
          >
            Designate
          </Button>
        </div>
      )}
    </div>
  );
};
